import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import type { IdTokenClaims, SessionTokens } from '../type';

/**
 * Refreshed this far before the access token actually expires, so a request that
 * takes a moment to reach the gateway does not arrive with a just-expired token.
 */
const EXPIRY_BUFFER_MS = 60_000;

type TokenCache = { tokens: SessionTokens; expiresAt: number };

/**
 * In-memory token cache.
 *
 * `fetchAuthSession()` is not free - it can hit the network - and the request
 * interceptor calls it on **every** request, so an uncached implementation makes
 * a table with eight parallel queries do eight session lookups. The legacy client
 * mirrored this cache into `localStorage` as well; that is dropped deliberately,
 * because Amplify v6 already persists the session and a second copy of a bearer
 * token in storage is a liability with no benefit.
 */
let cache: TokenCache | null = null;

/** De-dupes concurrent refreshes so a burst of requests triggers one fetch. */
let inFlight: Promise<SessionTokens | null> | null = null;

const readSession = async (forceRefresh: boolean): Promise<SessionTokens | null> => {
  const session = await fetchAuthSession({ forceRefresh });
  if (!session.tokens?.accessToken) return null;

  const tokens: SessionTokens = {
    idToken: session.tokens.idToken?.toString() ?? '',
    accessToken: session.tokens.accessToken.toString(),
  };

  const exp = session.tokens.accessToken.payload.exp;
  cache = typeof exp === 'number' ? { tokens, expiresAt: exp * 1000 - EXPIRY_BUFFER_MS } : null;

  return tokens;
};

/**
 * The current session tokens, or `null` when there is no usable session.
 *
 * Never throws: callers are request interceptors and route guards, and both want
 * "no session" as a value rather than an exception to catch.
 */
export const getSessionTokens = async ({
  forceRefresh = false,
} = {}): Promise<SessionTokens | null> => {
  if (!forceRefresh && cache && Date.now() < cache.expiresAt) return cache.tokens;
  if (inFlight && !forceRefresh) return inFlight;

  inFlight = readSession(forceRefresh)
    .catch(() => {
      cache = null;
      return null;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

export const getAccessToken = async (): Promise<string | null> =>
  (await getSessionTokens())?.accessToken ?? null;

export const getIdToken = async (): Promise<string | null> =>
  (await getSessionTokens())?.idToken ?? null;

/** Drops the cache. Call on sign-out, and after anything that changes claims. */
export const clearTokenCache = (): void => {
  cache = null;
  inFlight = null;
};

/** Decoded id-token claims, or `null`. Never throws on a malformed token. */
export const getIdTokenClaims = async (): Promise<IdTokenClaims | null> => {
  const tokens = await getSessionTokens();
  if (!tokens?.idToken) return null;
  try {
    return jwtDecode<IdTokenClaims>(tokens.idToken);
  } catch {
    return null;
  }
};

/** True when Amplify has a current user AND a usable token for them. */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    await getCurrentUser();
    return (await getSessionTokens()) !== null;
  } catch {
    return false;
  }
};

/**
 * The user's permission strings.
 *
 * The claim is a JSON-encoded array rather than a real array, so it is parsed
 * here and defended against: a malformed claim yields no permissions, which fails
 * closed.
 */
export const getPermissionList = async (): Promise<string[]> => {
  const claims = await getIdTokenClaims();
  if (!claims?.permissions) return [];
  try {
    const parsed: unknown = JSON.parse(claims.permissions);
    return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Whether the user has finished profile setup.
 *
 * Defaults to `true` when the claim is missing or unreadable: a user who cannot
 * be shown to be incomplete must not be trapped in the setup flow.
 */
export const isProfileSetupComplete = async (): Promise<boolean> => {
  const claims = await getIdTokenClaims();
  return claims?.profile_setup_status !== 'incomplete';
};
